import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { ExternalLink, ArrowLeft, Star, Newspaper, Share2, Youtube, BadgeCheck } from "lucide-react";
import { newsImageFor } from "@/lib/newsFallback";
import NoIndex from "@/components/NoIndex";

// Noindex on the IndianFamilyTree.com build only — flagged by the
// 2026-10-06 SEO audit as off-topic cross-site content on that domain.
const _NOINDEX_ON_IFT = (process.env.REACT_APP_SITE_SOURCE || "").toLowerCase() === "indianfamilytree";
import TranslatableText from "@/components/TranslatableText";
import { toast } from "sonner";
import NewsComments from "@/components/NewsComments";

function Stars({ n }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={13} className={i <= n ? "" : "opacity-25"} style={{ color: i <= n ? "var(--wofy-primary)" : "#999", fill: i <= n ? "var(--wofy-primary)" : "transparent" }} />
      ))}
    </span>
  );
}

export default function ThingsToKnowDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    http.get(`/news/${id}`)
      .then((r) => { setArticle(r.data.article); setRelated(r.data.related || []); })
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: article?.title, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    }
  };

  if (loading) {
    return <div data-testid="ttk-detail-loading" className="max-w-3xl mx-auto px-6 py-16" style={{ color: "var(--wofy-text-soft)" }}>Loading brief…</div>;
  }
  if (!article) {
    return (
      <div data-testid="ttk-detail-missing" className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-display text-3xl mb-4">Brief not found.</h1>
        <Link to="/things-to-know" className="underline" style={{ color: "var(--wofy-primary)" }}>Back to Today's briefing</Link>
      </div>
    );
  }

  const pub = new Date(article.published_at);
  const isCommunity = article.source === "community_submission";
  const ytId = (() => {
    if (!article.youtube_url) return null;
    const m = article.youtube_url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  })();

  return (
    <div data-testid="ttk-detail-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      {_NOINDEX_ON_IFT && <NoIndex />}
      <Link to="/things-to-know" className="inline-flex items-center gap-1 text-sm underline mb-6" style={{ color: "var(--wofy-primary)" }}>
        <ArrowLeft size={14} /> Back to Today's briefing
      </Link>

      <div className="overline mb-3 flex items-center gap-2 flex-wrap" style={{ color: "var(--wofy-primary)" }}>
        <span>{article.category}</span>
        {article.subcategory && (<><span>·</span><span>{article.subcategory}</span></>)}
        <span>·</span>
        <span>
          {article.source_name}
          {article.author_trusted && (
            <BadgeCheck className="inline w-3.5 h-3.5 -mt-0.5 ml-1" style={{ color: "#1D4ED8" }} aria-label="Trusted publisher" />
          )}
        </span>
        <span>·</span>
        <span>{pub.toLocaleString()}</span>
        <span>·</span>
        <Stars n={article.importance} />
      </div>

      <h1 className="font-display text-4xl md:text-5xl leading-tight mb-4">{article.title}</h1>

      <img
        src={newsImageFor(article)}
        alt=""
        className="w-full max-h-[420px] object-cover rounded-lg mb-6"
        data-testid="ttk-detail-hero-img"
        onError={(e) => {
          if (!e.currentTarget.dataset.fallback) {
            e.currentTarget.dataset.fallback = "1";
            e.currentTarget.src = "/news-fallbacks/default.svg";
          }
        }}
      />

      <TranslatableText
        text={article.ai_summary || article.short_description}
        testid="ttk-detail-summary"
        minChars={30}
        className="text-lg leading-relaxed mb-6"
      />

      {isCommunity && article.long_article && (
        <TranslatableText
          text={article.long_article}
          testid="ttk-detail-long-article"
          minChars={60}
          className="mb-8 text-base leading-relaxed"
        />
      )}

      {isCommunity && article.images?.length > 1 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-8">
          {article.images.slice(1).map((src, i) => (
            <img key={i} src={src} alt="" className="w-full h-40 object-cover rounded-lg" />
          ))}
        </div>
      )}

      {ytId && (
        <div className="mb-8 aspect-video rounded-lg overflow-hidden" style={{ border: "1px solid var(--wofy-line)" }}>
          <iframe
            title="Video"
            src={`https://www.youtube.com/embed/${ytId}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      )}

      {!isCommunity && (
        <div
          className="p-4 rounded-md border text-sm mb-8"
          style={{ borderColor: "var(--wofy-line)", background: "rgba(0,0,0,0.02)", color: "var(--wofy-text-soft)" }}
        >
          <strong>AI-generated summary.</strong> Social Pulse does not publish the full article. This brief is a neutral paraphrase by AI. Read the original at {article.source_name} using the link below.
        </div>
      )}

      <div className="flex gap-3 flex-wrap mb-10">
        {article.url && (
          <a
            data-testid="ttk-detail-read-original"
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold"
            style={{ background: "var(--wofy-primary)", color: "white" }}
          >
            {isCommunity ? "Visit source" : "Read Original Article"} <ExternalLink size={14} />
          </a>
        )}
        {article.youtube_url && !ytId && (
          <a
            href={article.youtube_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold border"
            style={{ borderColor: "var(--wofy-line)", color: "#B23A3A" }}
          >
            <Youtube size={14} /> Watch video
          </a>
        )}
        <button
          data-testid="ttk-detail-share"
          onClick={share}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold border"
          style={{ borderColor: "var(--wofy-line)" }}
        >
          <Share2 size={14} /> Share
        </button>
      </div>

      {related.length > 0 && (
        <section>
          <h2 className="font-display text-2xl mb-4">Related briefs</h2>
          <ul className="divide-y" style={{ borderColor: "var(--wofy-line)" }}>
            {related.map((r) => (
              <li key={r.id} className="py-3">
                <Link
                  to={`/things-to-know/${r.id}`}
                  data-testid={`ttk-related-${r.id}`}
                  className="flex items-start gap-3 group"
                >
                  <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: "var(--wofy-card-bg,#f2ede4)", border: "1px solid var(--wofy-line)" }}>
                    <img
                      src={newsImageFor(r)}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (!e.currentTarget.dataset.fallback) {
                          e.currentTarget.dataset.fallback = "1";
                          e.currentTarget.src = "/news-fallbacks/default.svg";
                        }
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--wofy-text-soft)" }}>{r.source_name}</div>
                    <div className="text-base leading-snug group-hover:underline">{r.title}</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {article && <NewsComments articleId={article.id} />}
    </div>
  );
}
